import { Component, Inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ProjectService } from '../../services/ProjectService/project';
import { AdminService } from '../../services/AdminService/admin';
import { CompanyResponse } from '../../models/company.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';

export interface AddParticipantDialogData {
  projectId: string;
  existingCompanyIds: string[];
}

@Component({
  selector: 'app-add-participant-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    ErrorBanner,
  ],
  templateUrl: './add-participant-dialog.html',
  styleUrl: './add-participant-dialog.scss',
})
export class AddParticipantDialog implements OnInit {
  companies = signal<CompanyResponse[]>([]);
  isLoadingCompanies = signal(false);

  selection: CompanyResponse | string = '';

  isSubmitting = signal(false);
  errorMessage = signal('');

  constructor(
    private dialogRef: MatDialogRef<AddParticipantDialog>,
    private projectService: ProjectService,
    private admin: AdminService,
    @Inject(MAT_DIALOG_DATA) private data: AddParticipantDialogData
  ) {}

  ngOnInit(): void {
    this.isLoadingCompanies.set(true);
    this.admin.listCompanies('APPROVED', 0, 100).subscribe({
      next: (res) => {
        this.isLoadingCompanies.set(false);
        this.companies.set(res.data.filter((c) => !this.data.existingCompanyIds.includes(c.id)));
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingCompanies.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load companies.'));
      },
    });
  }

  get filteredCompanies(): CompanyResponse[] {
    const term = typeof this.selection === 'string' ? this.selection.trim().toLowerCase() : '';
    if (!term) {
      return this.companies();
    }
    return this.companies().filter(
      (c) => c.name.toLowerCase().includes(term) || c.registrationNo.toLowerCase().includes(term)
    );
  }

  get companiesHint(): string {
    const count = this.companies().length;
    return `${count} approved ${count === 1 ? 'company' : 'companies'} to choose from`;
  }

  displayCompany = (company: CompanyResponse | string): string => {
    if (!company) {
      return '';
    }
    return typeof company === 'string'
      ? company
      : `${company.name} — ${company.registrationNo} (${company.countryCode})`;
  };

  get canSubmit(): boolean {
    return typeof this.selection === 'object' && !!this.selection;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }

    const company = this.selection as CompanyResponse;
    this.isSubmitting.set(true);
    this.errorMessage.set('');

    this.projectService.addParticipant(this.data.projectId, { registrationNo: company.registrationNo }).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to add participant.'));
      },
    });
  }
}